import { FamilyVerdict } from '../engine';

// Blueprint palette, matching the Dollhouse illustration (self-contained, no color mode).
const NAVY = '#162548';
const PAPER = '#dde5f7';
const GLOW = '#fec351';

const WIDTH = 1080;
const HEIGHT = 1350;
const MARGIN = 96;
const FONT = '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

type ShareInput = {
  /** outerHTML of the rendered DS.IconLogo — rasterized into the footer so the wordmark stays current */
  logoSvg?: string;
  scenarioName: string;
  verdict: FamilyVerdict;
};

/** rasterize the live wordmark SVG, tinted to the paper color; resolves null if anything goes wrong.
 *  An inline SVG's outerHTML lacks xmlns, dimensions, and its styled-components fill — all three are
 *  required for the blob to load as an image, so they're injected here. */
const loadLogo = (logoSvg?: string): Promise<HTMLImageElement | null> => {
  if (!logoSvg) return Promise.resolve(null);
  const viewBox = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(logoSvg);
  const ratio = viewBox ? Number(viewBox[1]) / Number(viewBox[2]) : 4;
  const rasterHeight = 88;
  const namespaced = logoSvg.includes('xmlns=')
    ? logoSvg
    : logoSvg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
  const tinted = namespaced
    .replace(/ fill="(?!none)[^"]*"/g, '')
    .replace('<svg', `<svg fill="${PAPER}" height="${rasterHeight}" width="${Math.round(rasterHeight * ratio)}"`);
  const url = URL.createObjectURL(new Blob([tinted], { type: 'image/svg+xml' }));
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
};

const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] =>
  text.split(' ').reduce<string[]>((lines, word) => {
    const current = lines[lines.length - 1];
    const candidate = current ? `${current} ${word}` : word;
    if (current && ctx.measureText(candidate).width > maxWidth) return [...lines, word];
    return [...lines.slice(0, -1), candidate];
  }, []);

const drawCard = (ctx: CanvasRenderingContext2D, { scenarioName, verdict }: ShareInput): void => {
  ctx.fillStyle = NAVY;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // faint drafting grid, like the dollhouse paper
  ctx.strokeStyle = PAPER;
  ctx.globalAlpha = 0.08;
  ctx.lineWidth = 1;
  Array.from({ length: Math.ceil(WIDTH / 60) }).forEach((_, i) => {
    ctx.beginPath();
    ctx.moveTo(i * 60, 0);
    ctx.lineTo(i * 60, HEIGHT);
    ctx.stroke();
  });
  Array.from({ length: Math.ceil(HEIGHT / 60) }).forEach((_, i) => {
    ctx.beginPath();
    ctx.moveTo(0, i * 60);
    ctx.lineTo(WIDTH, i * 60);
    ctx.stroke();
  });
  ctx.globalAlpha = 1;

  ctx.fillStyle = PAPER;
  ctx.textAlign = 'center';
  ctx.font = `600 40px ${FONT}`;
  ctx.fillText('BLACKOUT BATTLE', WIDTH / 2, MARGIN + 24);
  ctx.globalAlpha = 0.7;
  ctx.font = `400 34px ${FONT}`;
  ctx.fillText(scenarioName, WIDTH / 2, MARGIN + 76);
  ctx.globalAlpha = 1;

  ctx.font = `400 38px ${FONT}`;
  ctx.fillText('The family rates my blackout parenting', WIDTH / 2, 320);
  ctx.fillStyle = GLOW;
  ctx.font = `700 160px ${FONT}`;
  ctx.fillText(`${verdict.rating}/10`, WIDTH / 2, 480);

  ctx.textAlign = 'left';
  let y = 600;
  verdict.lines.forEach(({ name, text }) => {
    ctx.fillStyle = GLOW;
    ctx.font = `700 30px ${FONT}`;
    ctx.fillText(name.toUpperCase(), MARGIN, y);
    y += 48;
    ctx.fillStyle = PAPER;
    ctx.font = `400 38px ${FONT}`;
    wrapText(ctx, `“${text}”`, WIDTH - MARGIN * 2).forEach((line) => {
      ctx.fillText(line, MARGIN, y);
      y += 50;
    });
    y += 36;
  });

  ctx.textAlign = 'center';
  ctx.strokeStyle = PAPER;
  ctx.globalAlpha = 0.4;
  ctx.beginPath();
  ctx.moveTo(MARGIN, HEIGHT - 200);
  ctx.lineTo(WIDTH - MARGIN, HEIGHT - 200);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = PAPER;
  ctx.font = `600 34px ${FONT}`;
  ctx.fillText('Think you can parent a blackout better?', WIDTH / 2, HEIGHT - 140);
  ctx.fillStyle = GLOW;
  ctx.font = `600 30px ${FONT}`;
  ctx.fillText('aurorasolar.com/home-solar/blackout-battle', WIDTH / 2, HEIGHT - 90);
};

const drawLogo = (ctx: CanvasRenderingContext2D, logo: HTMLImageElement): void => {
  const logoHeight = 44;
  const ratio = logo.width > 0 && logo.height > 0 ? logo.width / logo.height : 4;
  const logoWidth = logoHeight * ratio;
  ctx.drawImage(logo, (WIDTH - logoWidth) / 2, HEIGHT - 72, logoWidth, logoHeight);
};

const toBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });

/** Renders the verdict card to a PNG file; resolves null when canvas 2d is unavailable (e.g. jsdom). */
export const renderVerdictFile = async (input: ShareInput): Promise<File | null> => {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  drawCard(ctx, input);
  const logo = await loadLogo(input.logoSvg);
  if (logo) drawLogo(ctx, logo);
  const blob = await toBlob(canvas);
  if (!blob) return null;

  return new File([blob], 'blackout-battle-verdict.png', { type: 'image/png' });
};

/**
 * Shares a pre-rendered card (Web Share API), falling back to a PNG download.
 * iOS Safari only allows navigator.share inside the user gesture's synchronous run, so render the
 * file ahead of the click and call this with nothing awaited in between.
 */
export const deliverVerdictFile = async (file: File): Promise<'downloaded' | 'shared'> => {
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: 'Blackout Battle' });
    return 'shared';
  }

  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.download = file.name;
  anchor.href = url;
  // Safari and Firefox only honor programmatic downloads from anchors attached to the document
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return 'downloaded';
};

/** Render-then-deliver fallback for when no pre-rendered file is available at click time. */
export const shareVerdictCard = async (input: ShareInput): Promise<'downloaded' | 'shared' | null> => {
  const file = await renderVerdictFile(input);
  return file ? deliverVerdictFile(file) : null;
};
